import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function Survey() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const preview = searchParams.get("preview") === "1";
  const [state, setState] = useState({ loading: true, error: null, survey: null, submitted: false, context: null, preview: false });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);

  const base = token ? `/survey/t/${token}` : null;

  useEffect(() => {
    (async () => {
      try {
        const url = token ? `/survey/t/${token}` : `/survey/active${preview ? "?preview=1" : ""}`;
        const { data } = await api.get(url);
        if (!data.survey) {
          setState({ loading: false, error: "This survey isn't available right now.", survey: null, submitted: false });
          return;
        }
        setState({ loading: false, error: null, survey: data.survey, submitted: data.submitted, context: data.context, preview: !!data.preview });
      } catch (err) {
        setState({ loading: false, error: err?.response?.data?.detail || "This survey link is invalid or has expired.", survey: null, submitted: false });
      }
    })();
    // eslint-disable-next-line
  }, [token, preview]);

  const submit = async () => {
    if (state.preview) { toast.info("Preview mode — responses are not saved."); return; }
    const s = state.survey;
    for (const q of s.questions) {
      if (q.required && (answers[q.id] === undefined || answers[q.id] === "" )) {
        toast.error("Please answer all required questions.");
        return;
      }
    }
    setBusy(true);
    try {
      const url = token ? `/survey/t/${token}/respond` : `/survey/${s.id}/respond`;
      const { data } = await api.post(url, { answers });
      setState((p) => ({ ...p, submitted: true, thankYou: data }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err?.response?.status === 409) { setState((p) => ({ ...p, submitted: true })); }
      else toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };

  if (state.loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-zinc-400" /></div>;

  if (state.error) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 px-6">
        <div className="max-w-md text-center" data-testid="survey-error">
          <h1 className="serif text-2xl">Survey unavailable</h1>
          <p className="text-zinc-500 mt-3 text-sm">{state.error}</p>
          <a href="/login" className="btn-outline-black h-11 px-6 inline-flex items-center mt-6 text-xs">Sign in to your portal</a>
        </div>
      </div>
    );
  }

  const s = state.survey;

  if (state.submitted) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50 px-6">
        <div className="max-w-lg text-center bg-white border border-zinc-200 p-10" data-testid="survey-thankyou">
          <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
          <h1 className="serif text-3xl mt-4">{s.thank_you_title || "Thank You"}</h1>
          <p className="text-zinc-600 mt-3 leading-relaxed">{s.thank_you_body}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4" data-testid="survey-page">
      {state.preview && (
        <div className="max-w-2xl mx-auto mb-3 bg-amber-100 border border-amber-300 text-amber-900 text-xs px-4 py-2 text-center tracking-wide" data-testid="survey-preview-banner">
          PREVIEW MODE — this is how participants will see the survey. Responses are not saved.
        </div>
      )}
      <div className="max-w-2xl mx-auto bg-white border border-zinc-200">
        <div className="p-6 sm:p-8 border-b border-zinc-100">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-400">Post-Event Survey</div>
          <h1 className="serif text-2xl sm:text-3xl mt-2 leading-tight">{s.title}</h1>
          {state.context && <p className="text-xs text-zinc-400 mt-2">{state.context.type_label}{state.context.company ? ` · ${state.context.company}` : ""}</p>}
          <p className="text-sm text-zinc-600 mt-4 leading-relaxed">{s.intro}</p>
        </div>
        <div className="p-6 sm:p-8 space-y-8">
          {s.questions.map((q, i) => (
            <div key={q.id} data-testid={`survey-q-${q.id}`}>
              <div className="text-sm font-medium text-zinc-900">
                {i + 1}. {q.text} {q.required && <span className="text-red-500">*</span>}
              </div>
              {q.type === "rating" && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-5 gap-2">
                    {q.labels.map((lab, idx) => {
                      const val = idx + 1;
                      const active = answers[q.id] === val;
                      return (
                        <button key={val} type="button" onClick={() => setAnswers((a) => ({ ...a, [q.id]: val }))}
                          data-testid={`survey-${q.id}-rate-${val}`}
                          className={`py-3 px-1 border text-center transition-colors ${active ? "bg-black text-white border-black" : "bg-white border-zinc-300 hover:border-black"}`}>
                          <div className="text-lg font-semibold">{val}</div>
                          <div className="text-[10px] leading-tight mt-1">{lab}</div>
                        </button>
                      );
                    })}
                  </div>
                  {q.na_option && (
                    <label className="flex items-center gap-2 text-sm text-zinc-600 mt-2 cursor-pointer">
                      <input type="radio" checked={answers[q.id] === "na"} onChange={() => setAnswers((a) => ({ ...a, [q.id]: "na" }))} data-testid={`survey-${q.id}-na`} />
                      {q.na_option}
                    </label>
                  )}
                </div>
              )}
              {q.type === "choice" && (
                <div className="mt-3 space-y-2">
                  {q.options.map((o) => (
                    <label key={o.value} className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${answers[q.id] === o.value ? "border-black bg-zinc-50" : "border-zinc-300 hover:border-black"}`}>
                      <input type="radio" name={q.id} checked={answers[q.id] === o.value} onChange={() => setAnswers((a) => ({ ...a, [q.id]: o.value }))} data-testid={`survey-${q.id}-opt-${o.value}`} />
                      <span className="text-sm">{o.label}</span>
                    </label>
                  ))}
                </div>
              )}
              {q.type === "text" && (
                <div className="mt-3">
                  <textarea rows={4} maxLength={q.max_length || 750}
                    value={answers[q.id] || ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                    data-testid={`survey-${q.id}-text`}
                    className="w-full border border-zinc-300 focus:border-black outline-none p-3 text-sm"
                    placeholder="Optional — share your thoughts" />
                  <div className="text-[11px] text-zinc-400 text-right mt-1">{(answers[q.id] || "").length}/{q.max_length || 750}</div>
                </div>
              )}
            </div>
          ))}
          <button onClick={submit} disabled={busy} className="btn-black h-12 w-full text-sm tracking-[0.14em]" data-testid="survey-submit">
            {busy ? "Submitting…" : (s.cta || "SHARE YOUR FEEDBACK")}
          </button>
        </div>
      </div>
    </div>
  );
}
